import { Component, OnInit, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { EmpresaListagemDTO } from '../../../models/configuracoes/empresa-listagem.model';

/**
 * Lista de Empresas (feature MASTER).
 *
 * Lista todas as empresas com busca (nome, RUC, telefone e e-mail) e paginacao,
 * e permite trocar a empresa ativa do usuario logado. Espelha a feature mobile
 * `lib/features/empresa`.
 *
 * Fluxo de troca: PATCH /usuario/alterar-empresa/{id} -> invalida cache da sessao
 * -> recarrega o app (a empresa ativa e resolvida server-side pelo usuario).
 */
@Component({
  selector: 'app-configuracao-empresas',
  templateUrl: './configuracao-empresas.component.html',
  styleUrls: ['./configuracao-empresas.component.scss'],
  providers: [MessageService],
})
export class ConfiguracaoEmpresasComponent implements OnInit, OnDestroy {
  // Objetos de traducao
  texts: any = {};
  labels: any = {};
  placeholders: any = {};
  botoes: any = {};
  tooltips: any = {};

  empresas: EmpresaListagemDTO[] = [];
  loading = true;

  // Paginacao (server-side)
  first = 0;
  rows = 10;
  totalRecords = 0;

  filterName = '';

  // Confirmacao de troca de empresa
  confirmDialogVisible = false;
  empresaSelecionada: EmpresaListagemDTO | null = null;
  trocandoEmpresa = false;

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  private search$ = new Subject<string>();
  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private empresaConfigService: EmpresaConfigService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.setupSearchDebounce();
    this.findEmpresas();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('configuracao-empresas.texts.titulo'),
      subtitulo: this.translate.instant('configuracao-empresas.texts.subtitulo'),
      tabela_de_empresas: this.translate.instant('configuracao-empresas.texts.tabela_de_empresas'),
      nenhuma_empresa: this.translate.instant('configuracao-empresas.texts.nenhuma_empresa'),
      dica_busca: this.translate.instant('configuracao-empresas.texts.dica_busca'),
      carregando: this.translate.instant('configuracao-empresas.texts.carregando'),
      confirmacao: this.translate.instant('common.confirmacao'),
      confirmar_troca: this.translate.instant('configuracao-empresas.texts.confirmar_troca'),
    };

    this.labels = {
      nome: this.translate.instant('configuracao-empresas.labels.nome'),
      ruc: this.translate.instant('configuracao-empresas.labels.ruc'),
      email: this.translate.instant('configuracao-empresas.labels.email'),
      telefone: this.translate.instant('configuracao-empresas.labels.telefone'),
      acoes: this.translate.instant('configuracao-empresas.labels.acoes'),
      itens_por_pagina: this.translate.instant('configuracao-empresas.labels.itens_por_pagina'),
    };

    this.placeholders = {
      pesquisar: this.translate.instant('configuracao-empresas.placeholders.pesquisar'),
    };

    this.botoes = {
      selecionar: this.translate.instant('configuracao-empresas.botoes.selecionar'),
      cancelar: this.translate.instant('common.cancelar'),
      confirmar: this.translate.instant('common.confirmar'),
    };

    this.tooltips = {
      selecionar: this.translate.instant('configuracao-empresas.tooltips.selecionar'),
    };
  }

  // =========================================================================
  // Busca
  // =========================================================================

  private setupSearchDebounce(): void {
    this.search$
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(() => {
        this.first = 0;
        this.findEmpresas(0);
      });
  }

  onSearchChange(value: string): void {
    this.filterName = value || '';
    this.search$.next(this.filterName);
  }

  // =========================================================================
  // Dados
  // =========================================================================

  findEmpresas(page: number = 0, size: number = this.rows): void {
    this.loading = true;
    this.empresaConfigService.listarEmpresas(this.filterName, page, size).subscribe({
      next: (response) => {
        this.empresas = response.content || [];
        this.totalRecords = response.totalElements || 0;
        this.first = page * size;
        this.loading = false;
      },
      error: () => {
        this.empresas = [];
        this.totalRecords = 0;
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('configuracao-empresas.texts.erro_carregar'),
        });
      },
    });
  }

  onPageChange(event: any): void {
    const page = event.page;
    this.rows = event.rows;
    this.findEmpresas(page, event.rows);
  }

  onRowsPerPageChange(): void {
    this.first = 0;
    this.findEmpresas(0, this.rows);
  }

  // =========================================================================
  // Troca de empresa
  // =========================================================================

  selecionarEmpresa(empresa: EmpresaListagemDTO): void {
    this.empresaSelecionada = empresa;
    this.confirmDialogVisible = true;
  }

  confirmarTroca(): void {
    if (!this.empresaSelecionada || this.empresaSelecionada.id == null) {
      return;
    }
    this.trocandoEmpresa = true;
    this.empresaConfigService.alterarEmpresa(this.empresaSelecionada.id).subscribe({
      next: () => {
        // Invalida o cache da sessao da empresa antes de recarregar o contexto.
        this.empresaConfigService.clearSessaoCache();
        this.confirmDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('configuracao-empresas.texts.troca_sucesso'),
        });
        // Recarrega a aplicacao para refletir a nova empresa ativa em todos os
        // caches em memoria (permissoes, sessao, carrinho, cotacao). Equivalente
        // ao reset completo feito no mobile ao trocar de empresa.
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 900);
      },
      error: (err) => {
        this.trocandoEmpresa = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('configuracao-empresas.texts.troca_erro'),
        });
      },
    });
  }

  cancelarTroca(): void {
    this.confirmDialogVisible = false;
    this.empresaSelecionada = null;
  }
}
